import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://greatercommunityimpact.org';
const OG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/39a48407-e3ab-4a34-863f-af86f0b611b2/images/421af611f8d4f8367a4d725baa1388f879a2707e23e71f6cf8dde57f230d127f.png';
const DEFAULT_KEYWORDS = 'Greater Community Impact, Savannah workforce development, workforce advancement, community impact, community services, collaboration, volunteer, Southeast Georgia, nonprofit, job fairs, housing stability, health wellness';

const PAGE_SEO = {
  '/': {
    title: 'Greater Community Impact | Strengthening Communities in Savannah & Southeast Georgia',
    description: 'Greater Community Impact (GCI) strengthens communities, mobilizes volunteers, and advances economic opportunity across Savannah, GA and Southeast Georgia through workforce development, health, housing, and collaboration programs.',
    keywords: `${DEFAULT_KEYWORDS}, homepage, Savannah GA community, economic mobility`,
  },
  '/about': {
    title: 'About Us | Greater Community Impact',
    description: 'Learn about Greater Community Impact\'s mission to strengthen communities and mobilize volunteers across Savannah and Southeast Georgia. Our programs create lasting change.',
    keywords: `${DEFAULT_KEYWORDS}, about, mission, vision, community organization`,
  },
  '/services': {
    title: 'Services | Greater Community Impact',
    description: 'Explore GCI\'s community services: workforce development, volunteer mobilization, health & wellness, housing stability, and community collaboration in Savannah, GA.',
    keywords: `${DEFAULT_KEYWORDS}, community programs, social services, support services`,
  },
  '/services/workforce': {
    title: 'Workforce Development | Greater Community Impact',
    description: 'Career readiness programs, job fairs, resume workshops, and employer partnerships to build economic mobility in Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, career readiness, job training, employment, resume workshop, job fair, career development`,
  },
  '/services/volunteer': {
    title: 'Volunteer Programs | Greater Community Impact',
    description: 'Join our volunteer network and make a difference in Savannah and Southeast Georgia. Skills-based matching, training, and community service opportunities.',
    keywords: `${DEFAULT_KEYWORDS}, volunteer opportunities, community service, give back, volunteer Savannah`,
  },
  '/services/collaboration': {
    title: 'Community Collaboration | Greater Community Impact',
    description: 'Collaborative meetings and focus areas bringing organizations together to address community challenges in Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, community collaboration, partnerships, collective impact, coalition`,
  },
  '/services/health': {
    title: 'Health & Wellness | Greater Community Impact',
    description: 'Community health programs, wellness resources, and health education for families in Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, health services, wellness programs, community health, mental health, preventive care`,
  },
  '/services/housing': {
    title: 'Housing Stability | Greater Community Impact',
    description: 'Housing assistance, rental education, and homeownership counseling for families in need across Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, housing assistance, rental help, homeownership, affordable housing`,
  },
  '/events': {
    title: 'Upcoming Events | Greater Community Impact',
    description: 'Join us at upcoming job fairs, workshops, volunteer opportunities, and community gatherings in Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, events, job fair, workshop, community events, Savannah events`,
  },
  '/impact-calendar': {
    title: 'Impact Calendar | Greater Community Impact',
    description: 'Browse the community impact calendar for events, workshops, and volunteer opportunities across Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, event calendar, community calendar, schedule, upcoming activities`,
  },
  '/donate': {
    title: 'Donate | Greater Community Impact',
    description: 'Support Greater Community Impact\'s mission to strengthen communities in Savannah and Southeast Georgia. Every donation makes a difference.',
    keywords: `${DEFAULT_KEYWORDS}, donate, giving, support, nonprofit donation, charitable giving`,
  },
  '/sponsorship': {
    title: 'Sponsorship | Greater Community Impact',
    description: 'Partner with Greater Community Impact as a sponsor. Support workforce development, community programs, and volunteer initiatives in Savannah.',
    keywords: `${DEFAULT_KEYWORDS}, sponsorship, corporate partnership, community investment`,
  },
  '/news': {
    title: 'News & Blog | Greater Community Impact',
    description: 'Latest news, stories, and updates from Greater Community Impact\'s programs and community initiatives in Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, news, blog, community stories, updates`,
  },
  '/testimonials': {
    title: 'Stories of Impact | Greater Community Impact',
    description: 'Read inspiring testimonials from individuals and families whose lives have been transformed through Greater Community Impact\'s programs.',
    keywords: `${DEFAULT_KEYWORDS}, testimonials, success stories, impact stories`,
  },
  '/resources': {
    title: 'Resource Library | Greater Community Impact',
    description: 'Free community resources, guides, and educational materials for workforce development, housing, health, and more in Savannah.',
    keywords: `${DEFAULT_KEYWORDS}, resources, guides, educational materials, community resources`,
  },
  '/contact': {
    title: 'Contact Us | Greater Community Impact',
    description: 'Get in touch with Greater Community Impact. We\'re here to help connect you with community resources in Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, contact, reach out, location, phone, email`,
  },
  '/appointments': {
    title: 'Book an Appointment | Greater Community Impact',
    description: 'Schedule an appointment with Greater Community Impact for workforce development, housing assistance, health resources, and more.',
    keywords: `${DEFAULT_KEYWORDS}, appointment, schedule, booking, consultation`,
  },
  '/partners': {
    title: 'Partner Directory | Greater Community Impact',
    description: 'Browse our network of community partners working together to strengthen Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, partners, organizations, community partners, directory`,
  },
  '/volunteer': {
    title: 'Volunteer Hub | Greater Community Impact',
    description: 'Join the GCI volunteer network. Find opportunities, track your impact, and make a difference in Savannah and Southeast Georgia.',
    keywords: `${DEFAULT_KEYWORDS}, volunteer hub, sign up, volunteer registration`,
  },
  '/privacy': {
    title: 'Privacy Policy | Greater Community Impact',
    description: 'Greater Community Impact\'s privacy policy - how we protect your personal information.',
  },
  '/terms': {
    title: 'Terms of Service | Greater Community Impact',
    description: 'Greater Community Impact\'s terms of service for website usage.',
  },
};

export default function SEOHead({ path = '/', title, description, keywords, image, noindex }) {
  const location = useLocation();
  const currentPath = path || location.pathname;
  const pageSeo = PAGE_SEO[currentPath] || PAGE_SEO['/'] || {};
  const pageTitle = title || pageSeo.title || 'Greater Community Impact | Strengthening Communities, Mobilizing Volunteers';
  const pageDesc = description || pageSeo.description || 'Greater Community Impact strengthens communities, mobilizes volunteers, and advances economic opportunity across Savannah, GA and Southeast Georgia.';
  const pageKeywords = keywords || pageSeo.keywords || DEFAULT_KEYWORDS;
  const pageImage = image || OG_IMAGE;
  const canonicalUrl = `${SITE_URL}${currentPath || ''}`;

  // Use imperative DOM manipulation for React 19 compatibility
  useEffect(() => {
    // Set title
    document.title = pageTitle;

    // Helper to set or update meta tags
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to set link tags
    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Set meta tags
    setMeta('description', pageDesc);
    setMeta('keywords', pageKeywords);
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:type', 'website', true);
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', pageDesc, true);
    setMeta('og:image', pageImage, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:site_name', 'Greater Community Impact', true);
    setMeta('og:locale', 'en_US', true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDesc);
    setMeta('twitter:image', pageImage);

    // Handle noindex
    if (noindex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content') === 'noindex, nofollow') {
        robotsMeta.remove();
      }
    }
  }, [pageTitle, pageDesc, pageKeywords, pageImage, canonicalUrl, noindex]);

  return null; // No Helmet JSX needed - using imperative DOM
}

export { PAGE_SEO, SITE_URL, OG_IMAGE };
